import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import InstagramFeed from '../components/InstagramFeed';
import TransformationSlider from '../components/TransformationSlider';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_CARDS = [
  { key: 'bath', image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/xv9ybmft_CasaPooch-85.jpg' },
  { key: 'styling', image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/zpeotx89_DSC01786.jpg' },
  { key: 'wellness', image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/rnjig6db_CasaPooch-84.jpg' },
  { key: 'seasonal', image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/y1htuzrf_IMG_8081.jpg' }
];

const FOUNDER_IMAGES = [
  { key: 'jessica', image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/e22jegmm_IMG_8149.jpg' },
  { key: 'together', image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/gnuzic6g_IMG_8211.jpg' },
  { key: 'brio', image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/3a4r44sk_CasaPooch-38.jpg' },
  { key: 'hunter', image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/l5lwnbiv_CasaPooch-45.jpg' }
];

function Home() {
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [reviewMeta, setReviewMeta] = useState({ rating: 0, total_reviews: 0 });
  const lang = i18n.language;

  const fetchReviews = useCallback(() => {
    axios.get(`${API_URL}/api/reviews`, { params: { lang } })
      .then(resp => {
        const allReviews = (resp.data.reviews || []).filter(r => r.text && r.text.trim().length > 0 && (r.rating || 5) >= 4);
        const shuffled = allReviews.sort(() => Math.random() - 0.5).slice(0, 3);
        setReviews(shuffled);
        setReviewMeta({ rating: resp.data.rating, total_reviews: resp.data.total_reviews });
      })
      .catch(() => {});
  }, [lang]);

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Home',
        page_path: window.location.pathname,
      });
    }

    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const fallbackReviews = t('testimonials.reviews', { returnObjects: true });
  const displayReviews = reviews.length >= 3
    ? reviews
    : [...reviews, ...fallbackReviews.slice(0, 3 - reviews.length)];

  return (
    <div className="home-page aritzia-style" data-testid="home-page">
      {/* Hero Video Section - Aritzia Style */}
      <section className="hero-video-section">
        <div className="video-container">
          <video
            ref={videoRef}
            className="hero-video"
            src="/hero-video.mp4"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-text">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="hero-title"
              data-testid="hero-title"
            >
              {t('home.heroTitle')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="hero-subtitle"
              data-testid="hero-subtitle"
            >
              {t('home.heroSubtitle')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <Link to="/book" className="hero-cta-btn" data-testid="hero-book-btn">
                <span className="hero-cta-text">{t('home.heroCta')}</span>
                <ArrowRight className="hero-cta-icon" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Services - Grid Style */}
      <section className="services-grid-section" data-testid="services-section">
        <div className="section-header">
          <span className="section-label">{t('home.servicesLabel')}</span>
          <h2 className="section-title">{t('home.servicesTitle')}</h2>
        </div>

        <div className="services-grid">
          {SERVICE_CARDS.map(card => (
            <Link to="/services" className="service-card" key={card.key} data-testid={`home-service-${card.key}`}>
              <div className="service-image-wrapper">
                <img 
                  src={card.image}
                  alt={t(`home.services.${card.key}.title`)}
                  className="service-image"
                  loading="lazy"
                />
              </div>
              <div className="service-info">
                <h3 className="service-title">{t(`home.services.${card.key}.title`)}</h3>
                <p className="service-desc">{t(`home.services.${card.key}.desc`)}</p>
              </div>
            </Link>
          ))}
        </div>

        <div className="section-cta">
          <Link to="/services" className="view-all-btn" data-testid="home-view-all-services">
            {t('home.viewAll')} <ArrowRight className="inline w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* About Preview - Full Width Image */}
      <section className="about-preview-section" data-testid="about-preview-section">
        <div className="about-content">
          <span className="section-label">{t('home.storyLabel')}</span>
          <h2 className="section-title">{t('home.storyTitle')}</h2>
          <p className="about-text">{t('home.storyText')}</p>
          <Link to="/about" className="text-link" data-testid="home-learn-more">
            {t('home.learnMore')} <ArrowRight className="inline w-4 h-4 ml-1" />
          </Link>
        </div>
      </section>

      {/* Before & After Transformations */}
      <TransformationSlider />

      {/* Google Reviews - Live */}
      <section className="testimonials-section" data-testid="testimonials-section">
        <div className="section-header">
          <span className="section-label">{t('home.reviewsLabel')}</span>
          <h2 className="section-title">{t('home.reviewsTitle')}</h2>
          {reviewMeta.total_reviews > 0 && (
            <div className="review-meta">
              <div className="review-meta-stars">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="star">{i < Math.round(reviewMeta.rating) ? '★' : '☆'}</span>
                ))}
              </div>
              <span className="review-meta-text" data-testid="review-meta-text">
                {t('home.reviewsMeta', { rating: reviewMeta.rating, count: reviewMeta.total_reviews })}
              </span>
            </div>
          )}
        </div>

        <div className="testimonials-grid">
          {displayReviews.map((review, idx) => (
            <div key={review.author ? `${review.author}-${idx}` : `review-${idx}`} className="testimonial-card" data-testid={`testimonial-${idx}`}>
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="star">{i < (review.rating || 5) ? '★' : '☆'}</span>
                ))}
              </div>
              <p className="testimonial-text">"{review.text && review.text.length > 200 ? review.text.substring(0, 200) + '...' : review.text}"</p>
              <p className="testimonial-author">— {review.author}{review.time ? `, ${review.time}` : ''}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Live Instagram Feed */}
      <InstagramFeed />

      {/* Meet the Founders */}
      <section className="founders-section" data-testid="founders-section">
        <div className="section-header">
          <span className="section-label">{t('home.teamLabel')}</span>
          <h2 className="section-title">{t('home.teamTitle')}</h2>
          <p className="section-subtitle">{t('home.teamSubtitle')}</p>
        </div>

        <div className="founders-grid">
          {FOUNDER_IMAGES.map(item => (
            <div className="founder-image-card" key={item.key}>
              <img 
                src={item.image}
                alt={t(`home.alts.${item.key}`)}
                className="founder-image"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        <div className="section-cta">
          <Link to="/about" className="view-all-btn" data-testid="home-read-story">
            {t('home.readStory')} <ArrowRight className="inline w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="booking-cta-section" data-testid="booking-cta-section">
        <div className="cta-content">
          <h2 className="cta-title">{t('home.ctaTitle')}</h2>
          <p className="cta-text">{t('home.ctaText')}</p>
          <Link to="/book" className="cta-button" data-testid="bottom-book-btn">
            {t('home.ctaButton')}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
